import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Mail, MessageCircle, Save, Pencil } from "lucide-react";
import { normalizePhoneE164 } from "@/lib/utils";

/** Recapiti editabili (E-mail, Telefono/WhatsApp) nel pop-up Dettaglio & Note Accordi dell'Ambassador.
 *  Aggiorna SEMPRE il profilo esistente (stesso _id): nessun duplicato. */
export const AmbassadorContactsEditor = ({ amb, onSaved }) => {
  const [email, setEmail] = useState(amb?.email || "");
  const [whatsapp, setWhatsapp] = useState(amb?.whatsapp || "");
  const [saving, setSaving] = useState(false);
  const synced = useRef({ id: amb?._id, email: amb?.email || "", whatsapp: amb?.whatsapp || "" });

  // Riallinea i campi quando cambia il profilo o arrivano dati aggiornati dal server,
  // ma NON sovrascrive ciò che l'operatore sta digitando.
  useEffect(() => {
    const next = { id: amb?._id, email: amb?.email || "", whatsapp: amb?.whatsapp || "" };
    const prev = synced.current;
    const untouched = (email || "") === prev.email && (whatsapp || "") === prev.whatsapp;
    if (next.id !== prev.id || untouched) { setEmail(next.email); setWhatsapp(next.whatsapp); }
    synced.current = next;
  }, [amb?._id, amb?.email, amb?.whatsapp]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = (email || "").trim() !== (amb?.email || "") || (whatsapp || "").trim() !== (amb?.whatsapp || "");

  const save = async () => {
    const e = (email || "").trim();
    const w = (whatsapp || "").trim();
    if (e && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { toast.error("Indirizzo e-mail non valido"); return; }
    const wNorm = w ? normalizePhoneE164(w, amb?.nation || "Italia") : "";
    if (w && !wNorm) { toast.error("Numero WhatsApp non valido: usa il formato internazionale (es. +39 348 1234567)"); return; }
    setSaving(true);
    try {
      await api.patch(`/ambassadors/${amb._id}`, { email: e, whatsapp: wNorm });
      setEmail(e); setWhatsapp(wNorm);
      synced.current = { id: amb?._id, email: e, whatsapp: wNorm };
      toast.success("Recapiti aggiornati sul profilo esistente");
      onSaved?.({ ...amb, email: e, whatsapp: wNorm });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Salvataggio recapiti fallito");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-2 rounded-lg border border-border bg-brema-bg/40 p-3" data-testid="amb-contacts-editor">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
          <Pencil className="h-3 w-3 text-brand" /> Recapiti · modificabili
        </p>
        {dirty && <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-600 dark:text-amber-300" data-testid="amb-contacts-unsaved">Modifiche non salvate</span>}
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="space-y-1">
          <Label className="flex items-center gap-1 text-xs text-muted-foreground"><Mail className="h-3 w-3" /> E-mail</Label>
          <Input data-testid="amb-profile-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && dirty && !saving) save(); }}
            placeholder="nome@esempio.com" className="h-8 text-sm" />
        </div>
        <div className="space-y-1">
          <Label className="flex items-center gap-1 text-xs text-muted-foreground"><MessageCircle className="h-3 w-3" /> Telefono / WhatsApp</Label>
          <Input data-testid="amb-profile-whatsapp" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && dirty && !saving) save(); }}
            onBlur={() => { const n = normalizePhoneE164(whatsapp, amb?.nation || "Italia"); if (n) setWhatsapp(n); }}
            placeholder="+39 348 1234567" className="h-8 text-sm" />
        </div>
      </div>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-muted-foreground">Le modifiche aggiornano questo profilo (nessun duplicato) e vengono usate dagli invii successivi. Numero in E.164 (es. +393481234567).</p>
        <Button type="button" size="sm" onClick={save} disabled={saving || !dirty} data-testid="amb-profile-save-contacts"
          className="shrink-0 rounded-full bg-brand text-xs font-semibold text-white hover:bg-brand/90 disabled:opacity-40">
          {saving ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />} Salva recapiti
        </Button>
      </div>
    </div>
  );
};
